import {
  Check,
  ChevronDown,
  CircleX,
  Copy,
  Eye,
  EyeOff,
  Home,
  Lock,
  LogOut,
  Mail,
  RefreshCw,
  Settings,
  ShieldCheck,
  Shredder,
  SquarePen,
  Trash2,
  UserPlus,
  Users,
  UserX,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useQueryClient } from "../hooks/useDataCache";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { clearLastKnownHousehold } from "../hooks/useHousehold";
import {
  ALL_HOUSEHOLDS_QUERY_KEY,
  useHouseholds,
} from "../hooks/useHouseholds";
import { useLicense } from "../hooks/useLicense";
import { usePresenceContext } from "../hooks/usePresenceContext";
import {
  clearLastKnownHouseholdId,
  USER_HOUSEHOLD_ID_KEY,
  useUser,
} from "../hooks/useUser";
import { markExplicitSignOut } from "../lib/authSignals";
import { supabase } from "../lib/supabase";
import { isValidEmail } from "../lib/validation";
import {
  createHousehold,
  deleteCurrentUser,
  deleteHousehold,
  getHouseholdMembers,
  getUserHousehold,
  type Household,
  type HouseholdMember,
  isUserHouseholdAdmin,
  removeMember,
  updateHouseholdName,
} from "../services/householdService";
import {
  createInvitation,
  getHouseholdInvitations,
  getInvitationLink,
  type HouseholdInvitation,
  resendInvitation,
  revokeInvitation,
} from "../services/invitationService";
import { updateUserPassword } from "../services/userService";

// Maximum number of members allowed per household (1 admin + 5 members).
// Also used to derive the pending-invitation cap so both stay in sync.
const MAX_HOUSEHOLD_MEMBERS = 6;

// Props for the household settings modal exposed to parent components
interface HouseholdSettingsProps {
  onClose: () => void;
  onHouseholdUpdate?: (household: Household) => void;
  onLoadingChange?: (loading: boolean) => void;
}

// Modal component that manages household details, membership, invitations, and account actions
// Keeps local state, performs side effects, and exposes callbacks for parent components
export default function HouseholdSettings({
  onClose,
  onHouseholdUpdate,
  onLoadingChange,
}: HouseholdSettingsProps) {
  // Initialize presence tracking
  const { getUserPresence } = usePresenceContext();
  const { license, isLoading: licenseLoading } = useLicense();
  const queryClient = useQueryClient();
  const { householdId } = useUser();
  const { households, switchHousehold } = useHouseholds();

  const [household, setHousehold] = useState<Household | null>(null);
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  const [invitations, setInvitations] = useState<HouseholdInvitation[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const [newHouseholdName, setNewHouseholdName] = useState("");
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [confirmingMemberId, setConfirmingMemberId] = useState<string | null>(
    null,
  );
  const [creatingHousehold, setCreatingHousehold] = useState(false);
  const [newHouseholdNameInput, setNewHouseholdNameInput] = useState("");
  const [confirmingDeleteAccount, setConfirmingDeleteAccount] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [confirmingLeaveHousehold, setConfirmingLeaveHousehold] =
    useState(false);
  const [leavingHousehold, setLeavingHousehold] = useState(false);
  const [confirmingDeleteHousehold, setConfirmingDeleteHousehold] =
    useState(false);
  const [deletingHousehold, setDeletingHousehold] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  // Load household data, members, and pending invitations, and determine current user role
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      onLoadingChange?.(true);
      // Reset stale state before loading so the modal doesn't briefly show
      // the previous household's data while the new one is fetching.
      setHousehold(null);
      setMembers([]);
      setInvitations([]);
      setIsAdmin(false);
      setInitialLoadComplete(false);
      const householdData = await getUserHousehold(householdId ?? undefined);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user && !cancelled) {
        setCurrentUserId(user.id);
      }

      if (householdData) {
        if (!cancelled) {
          setHousehold(householdData);
          setNewHouseholdName(householdData.name);
        }

        let isUserAdmin = false;
        if (user) {
          isUserAdmin = await isUserHouseholdAdmin(user.id, householdData.id);
          if (!cancelled) setIsAdmin(isUserAdmin);
        }

        const membersData = await getHouseholdMembers(householdData.id);
        if (!cancelled) setMembers(membersData);

        // Only fetch invitations if user is an admin
        if (isUserAdmin) {
          const invitationsData = await getHouseholdInvitations(
            householdData.id,
          );
          const pendingInvitations = invitationsData.filter(
            (inv) =>
              inv.status === "pending" && new Date(inv.expires_at) > new Date(),
          );
          if (!cancelled) setInvitations(pendingInvitations);
        }
      } else {
        if (!cancelled) setHousehold(null);
      }
      if (!cancelled) {
        setInitialLoadComplete(true);
        onLoadingChange?.(false);
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [onLoadingChange, householdId]);

  useFocusTrap(modalRef, onClose);

  // Close the household switcher dropdown on outside click or Escape
  useEffect(() => {
    if (!switcherOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (
        switcherRef.current &&
        !switcherRef.current.contains(e.target as Node)
      ) {
        setSwitcherOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSwitcherOpen(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [switcherOpen]);

  // Send an invitation to the given email and optimistically update local invitations list
  const handleInvite = async () => {
    if (!household || !inviteEmail.trim()) return;

    setInviting(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setInviting(false);
      return;
    }

    try {
      const result = await createInvitation(household.id, inviteEmail, user.id);
      if (result.invitation) {
        const { invitation } = result;
        toast.success(`Invitation sent to ${inviteEmail}`);
        setInviteEmail("");
        // Optimistically add the new invitation to state
        setInvitations((prev) => [...prev, invitation]);
      } else {
        toast.error(
          result.error || "Failed to send invitation. Please try again.",
        );
      }
    } catch {
      toast.error("An error occurred while sending the invitation.");
    } finally {
      setInviting(false);
    }
  };

  // Copy a shareable invitation link for a token to the clipboard and show feedback.
  // navigator.clipboard.writeText() requires a secure context (https://) and is
  // unavailable in the Capacitor Android WebView, which runs on a custom scheme.
  // We fall back to the legacy execCommand approach for that environment.
  const handleCopyLink = async (token: string) => {
    const link = getInvitationLink(token);
    let copied = false;

    // Preferred: async Clipboard API (web / iOS)
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(link);
        copied = true;
      } catch {
        // Fall through to execCommand fallback
      }
    }

    // Fallback: textarea + execCommand (Android WebView)
    if (!copied) {
      const textarea = document.createElement("textarea");
      textarea.value = link;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        copied = document.execCommand("copy");
      } finally {
        document.body.removeChild(textarea);
      }
    }

    if (copied) {
      setCopiedToken(token);
      toast.success("Invitation link copied to clipboard!");
      setTimeout(() => setCopiedToken(null), 2000);
    } else {
      toast.error("Could not copy link. Please copy it manually.");
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    const success = await revokeInvitation(invitationId);
    if (success) {
      toast.success("Invitation revoked successfully");
      // Optimistically remove the invitation from state
      setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
    } else {
      toast.error("Failed to revoke invitation.");
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    setResendingId(invitationId);
    try {
      const result = await resendInvitation(invitationId);
      if (result.success) {
        toast.success("Invitation resent successfully!");
        // No need to reload - invitation state doesn't change visually
      } else {
        toast.error(result.error || "Failed to resend invitation.");
      }
    } finally {
      setResendingId(null);
    }
  };

  const handleUpdateName = async () => {
    if (!household || !newHouseholdName.trim()) return;

    const success = await updateHouseholdName(household.id, newHouseholdName);
    if (success) {
      toast.success(`Household name updated to "${newHouseholdName}"`);
      setEditingName(false);
      // Optimistically update household name in state
      const updatedHousehold = { ...household, name: newHouseholdName };
      setHousehold(updatedHousehold);
      // Invalidate household caches to refresh name across the app
      queryClient.invalidateQueries({
        queryKey: ["household", household.id],
      });
      queryClient.invalidateQueries({
        queryKey: ALL_HOUSEHOLDS_QUERY_KEY,
      });
      // Notify parent component of the update
      onHouseholdUpdate?.(updatedHousehold);
    } else {
      toast.error("Failed to update household name. Please try again.");
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    setRemovingMemberId(memberId);
    const success = await removeMember(memberId);
    if (success) {
      toast.success("Member removed from household");
      // Optimistically remove member from state
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } else {
      toast.error("Failed to remove member. Please try again.");
    }
    setRemovingMemberId(null);
    setConfirmingMemberId(null);
  };

  const handleCreateHousehold = async () => {
    if (!newHouseholdNameInput.trim() || !currentUserId) return;

    // Wait until the license status has finished loading before checking it.
    // If we check while licenseLoading is true, license is undefined and the
    // paywall would show incorrectly.
    if (licenseLoading) {
      toast.error(
        "Still checking license status, please try again in a moment.",
      );
      return;
    }

    // Check if user has a license to create households
    if (!license?.canCreateHousehold) {
      toast.error("A license is required to create households.");
      return;
    }

    setCreatingHousehold(true);
    const newHousehold = await createHousehold(
      newHouseholdNameInput,
      currentUserId,
    );
    if (newHousehold) {
      toast.success(
        `Household "${newHouseholdNameInput}" created successfully!`,
      );
      setNewHouseholdNameInput("");
      // Clear any stale active-household override so the queryFn picks up
      // the newly created household from the DB rather than an old session's ID.
      localStorage.removeItem("pantry_active_household_id");
      // Invalidate the allHouseholds list so the switcher includes the new one.
      queryClient.invalidateQueries({ queryKey: ALL_HOUSEHOLDS_QUERY_KEY });
      // Seed the cache with the new household ID immediately so useHousehold
      // doesn't wait for a round-trip before rendering the dashboard.
      queryClient.setQueryData(USER_HOUSEHOLD_ID_KEY, String(newHousehold.id));
      queryClient.invalidateQueries({
        queryKey: ["household", String(newHousehold.id)],
      });
      // Notify parent component of the update
      onHouseholdUpdate?.(newHousehold);
      onClose();
    } else {
      toast.error("Failed to create household. Please try again.");
    }
    setCreatingHousehold(false);
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all password fields.");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setUpdatingPassword(true);
    const success = await updateUserPassword(newPassword);

    if (success) {
      toast.success("Password updated successfully!");
      setChangingPassword(false);
      setNewPassword("");
      setConfirmPassword("");
    } else {
      toast.error("Failed to update password. Please try again.");
    }
    setUpdatingPassword(false);
  };

  // Clears all cached references to a household after leaving or deleting it
  const clearHouseholdFromCache = async (householdId: string) => {
    clearLastKnownHousehold();
    clearLastKnownHouseholdId();
    const stored = localStorage.getItem("pantry_active_household_id");
    if (stored === String(householdId)) {
      localStorage.removeItem("pantry_active_household_id");
    }
    queryClient.removeQueries({ queryKey: ["household", householdId] });
    queryClient.removeQueries({ queryKey: USER_HOUSEHOLD_ID_KEY });
    await queryClient.invalidateQueries({ queryKey: ALL_HOUSEHOLDS_QUERY_KEY });
  };

  // Remove the current user from their household, retaining their account
  const handleLeaveHousehold = async () => {
    if (!currentUserId) return;

    const myMember = members.find((m) => m.user_id === currentUserId);
    if (!myMember) return;

    setLeavingHousehold(true);

    try {
      const success = await removeMember(myMember.id);
      if (success) {
        toast.success("You have left the household.");
        await clearHouseholdFromCache(household?.id ?? "");
        onClose();
      } else {
        toast.error("Failed to leave household. Please try again.");
      }
    } catch (error) {
      console.error("🔴 Error leaving household:", error);
      toast.error("Failed to leave household. Please try again.");
    } finally {
      setLeavingHousehold(false);
      setConfirmingLeaveHousehold(false);
    }
  };

  // Delete the current household and all related data via DB cascade
  // The admin's account is retained; they are shown the create household screen
  const handleDeleteHousehold = async () => {
    if (!household) return;

    setDeletingHousehold(true);

    try {
      const success = await deleteHousehold(household.id);
      if (success) {
        toast.success("Household deleted successfully.");
        await clearHouseholdFromCache(household.id);
        onClose();
      } else {
        toast.error("Failed to delete household. Please try again.");
      }
    } catch (error) {
      console.error("🔴 Error deleting household:", error);
      toast.error("Failed to delete household. Please try again.");
    } finally {
      setDeletingHousehold(false);
      setConfirmingDeleteHousehold(false);
    }
  };

  // Delete the current user's account and, if admin, their household as well
  // Performs optimistic feedback and handles cleanup via sign-out
  const handleDeleteAccount = async () => {
    if (!currentUserId) return;

    setDeletingAccount(true);

    try {
      // If user is an admin of any households, delete all of them.
      // With multi-household support a user may be admin of several;
      // deleting only the currently-viewed one would leave orphaned households.
      const adminHouseholds = households.filter((h) => h.role === "admin");
      for (const h of adminHouseholds) {
        const householdDeleted = await deleteHousehold(h.id);
        if (!householdDeleted) {
          console.error(`🔴 Failed to delete household ${h.id}.`);
          toast.error("Failed to delete a household. Please try again.");
          setDeletingAccount(false);
          setConfirmingDeleteAccount(false);
          return;
        }
      }

      // Delete the user's account
      const userDeleted = await deleteCurrentUser();
      if (userDeleted) {
        toast.success("Account deleted successfully. Goodbye!");
        // Sign out the user (suppress expected 403 error since user no longer exists)
        try {
          markExplicitSignOut();
          await supabase.auth.signOut();
        } catch {
          // Expected error: user_not_found - the user was just deleted
          // Silently ignore this error as the auth state will update automatically
        }
        // The app should redirect to login automatically
      } else {
        console.error("🔴 Failed to delete user account.");
        toast.error("Failed to delete account. Please try again.");
      }
    } catch (error) {
      console.error("🔴 Error deleting account:", error);
    } finally {
      setDeletingAccount(false);
      setConfirmingDeleteAccount(false);
    }
  };

  // Don't render anything until initial load is complete
  if (!initialLoadComplete) {
    return null;
  }

  // Show create household form if user has no household
  if (!household) {
    return createPortal(
      <div
        className="pt-safe fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-household-title"
      >
        <div
          ref={modalRef}
          className="w-full max-w-2xl rounded-lg bg-white shadow-xl dark:bg-gray-800"
        >
          <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <Home
                className="h-6 w-6 text-blue-600 dark:text-blue-400"
                aria-hidden="true"
              />
              <h2
                id="create-household-title"
                className="text-xl font-bold text-gray-800 dark:text-gray-100"
              >
                Create Household
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close dialog"
            >
              <X
                className="h-6 w-6 text-gray-500 dark:text-gray-400"
                aria-hidden="true"
              />
            </button>
          </div>

          <div className="p-6">
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              You are not currently a member of any household. Create a new one
              to get started or beg the person who kicked you out to send you
              another invite. 😗
            </p>
            <div>
              <label
                htmlFor="newHouseholdName"
                className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Household Name
              </label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  id="newHouseholdName"
                  type="text"
                  value={newHouseholdNameInput}
                  onChange={(e) => setNewHouseholdNameInput(e.target.value)}
                  placeholder="e.g., Smith Family"
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newHouseholdNameInput.trim()) {
                      handleCreateHousehold();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleCreateHousehold}
                  disabled={creatingHousehold || !newHouseholdNameInput.trim()}
                  className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-opacity hover:bg-blue-700 disabled:pointer-events-none disabled:opacity-50 sm:w-auto"
                >
                  {creatingHousehold ? "Creating..." : "Create Household"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className="pt-safe fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
    >
      <div
        ref={modalRef}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-gray-800"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-3">
            <Settings
              className="h-6 w-6 text-blue-600 dark:text-blue-400"
              aria-hidden="true"
            />
            <h2
              id="settings-title"
              className="text-xl font-bold text-gray-800 dark:text-gray-100"
            >
              Household Settings
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close dialog"
          >
            <X
              className="h-6 w-6 text-gray-500 dark:text-gray-400"
              aria-hidden="true"
            />
          </button>
        </div>

        <div className="space-y-6 p-6">
          <div>
            <label
              htmlFor="householdName"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Household Name
            </label>
            {editingName && isAdmin ? (
              <div className="flex flex-col gap-3 sm:h-10 sm:flex-row">
                <input
                  id="householdName"
                  type="text"
                  value={newHouseholdName}
                  onChange={(e) => setNewHouseholdName(e.target.value)}
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
                <button
                  type="button"
                  onClick={handleUpdateName}
                  className="rounded-lg bg-blue-600 px-4 py-2 font-medium whitespace-nowrap text-white hover:bg-blue-700"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingName(false);
                    setNewHouseholdName(household?.name || "");
                  }}
                  className="rounded-lg bg-gray-200 px-4 py-2 font-medium whitespace-nowrap text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex h-10 items-center justify-between gap-2">
                {households.length > 1 ? (
                  <div ref={switcherRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setSwitcherOpen((prev) => !prev)}
                      className="flex items-center gap-1.5 py-2 text-xl leading-tight font-semibold text-gray-800 transition-colors hover:text-blue-600 dark:text-gray-100 dark:hover:text-blue-400"
                      aria-haspopup="listbox"
                      aria-expanded={switcherOpen}
                      aria-label={`Active household: ${household?.name}. Click to switch.`}
                    >
                      <span>{household?.name}</span>
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 ${switcherOpen ? "rotate-180" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                    {switcherOpen && (
                      <div
                        role="listbox"
                        aria-label="Select household"
                        className="absolute top-full left-0 z-50 mt-1 flex w-64 flex-col gap-1 rounded-lg border border-gray-300 bg-gray-100 p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
                      >
                        {households.map((h) => {
                          const isActive = String(h.id) === householdId;
                          return (
                            <button
                              key={h.id}
                              type="button"
                              role="option"
                              aria-selected={isActive}
                              onClick={async () => {
                                if (!isActive) {
                                  await switchHousehold(String(h.id));
                                }
                                setSwitcherOpen(false);
                              }}
                              className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                                isActive
                                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                  : "text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-blue-900"
                              }`}
                            >
                              <span className="flex min-w-0 flex-1 flex-col">
                                <span className="truncate font-medium">
                                  {h.name}
                                </span>
                                <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                  {h.role === "admin" ? (
                                    <>
                                      <ShieldCheck
                                        className="h-3 w-3"
                                        aria-hidden="true"
                                      />
                                      Admin
                                    </>
                                  ) : (
                                    <>
                                      <Users
                                        className="h-3 w-3"
                                        aria-hidden="true"
                                      />
                                      Member
                                    </>
                                  )}
                                </span>
                              </span>
                              {isActive && (
                                <Check
                                  className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400"
                                  aria-hidden="true"
                                />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="py-2 text-xl leading-tight font-semibold text-gray-800 dark:text-gray-100">
                    {household?.name}
                  </p>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setEditingName(true)}
                    className="flex shrink-0 items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
                    title="Edit household name"
                  >
                    <SquarePen className="h-4 w-4" />
                    Edit
                  </button>
                )}
              </div>
            )}
          </div>

          <div>
            <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
              <Users className="h-5 w-5" />
              Users ({members.length})
            </h3>
            <div className="space-y-2">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700"
                >
                  {confirmingMemberId === member.id ? (
                    <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        Remove {member.email}?
                      </p>
                      <div className="flex w-full gap-2 sm:ml-4 sm:w-auto">
                        <button
                          type="button"
                          onClick={() => handleRemoveMember(member.id)}
                          disabled={removingMemberId === member.id}
                          className="rounded-lg bg-red-600 px-3 py-1 text-sm font-medium text-white hover:bg-red-700"
                        >
                          {removingMemberId === member.id
                            ? "Removing..."
                            : "Yes"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmingMemberId(null)}
                          disabled={removingMemberId === member.id}
                          className="rounded-lg bg-gray-200 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-baseline gap-3">
                        <div className="relative flex items-center">
                          {getUserPresence(member.user_id)?.length > 0 ? (
                            <div className="relative flex h-3 w-3">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                              <span className="relative inline-flex h-3 w-3 rounded-full bg-green-500"></span>
                            </div>
                          ) : (
                            <span className="inline-flex h-3 w-3 rounded-full bg-gray-400 transition-colors dark:bg-gray-600"></span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 dark:text-gray-100">
                            {member.email}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {member.role === "admin" ? "Admin" : "Member"}
                          </p>
                        </div>
                      </div>
                      {isAdmin && member.user_id !== currentUserId && (
                        <button
                          type="button"
                          onClick={() => setConfirmingMemberId(member.id)}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-gray-600"
                          title="Remove member"
                        >
                          <UserX className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {isAdmin && (
            <div>
              <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                <UserPlus className="h-5 w-5" />
                Invite Member
              </h3>
              <div className="flex flex-col gap-3 sm:flex-row">
                <label htmlFor="inviteEmail" className="sr-only">
                  Email address
                </label>
                <input
                  id="inviteEmail"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Email address"
                  pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                  title="Please enter a valid email address"
                  autoComplete="off"
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
                <button
                  type="button"
                  onClick={handleInvite}
                  disabled={
                    inviting ||
                    !inviteEmail.trim() ||
                    !isValidEmail(inviteEmail) ||
                    members.length >= MAX_HOUSEHOLD_MEMBERS
                  }
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-opacity hover:bg-blue-700 disabled:pointer-events-none disabled:opacity-50"
                >
                  <Mail className="h-4 w-4" />
                  {inviting ? "Sending..." : "Invite"}
                </button>
              </div>
              {members.length >= MAX_HOUSEHOLD_MEMBERS && (
                <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                  This household is full ({MAX_HOUSEHOLD_MEMBERS} members
                  maximum). Remove a member to invite someone new.
                </p>
              )}
            </div>
          )}

          {isAdmin && invitations.length > 0 && (
            <div>
              <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                <Mail className="h-5 w-5" />
                Pending Invitations ({invitations.length})
              </h3>
              <div className="space-y-2">
                {invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-gray-700"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-gray-800 dark:text-gray-100">
                        {invitation.email}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Expires:{" "}
                        {new Date(invitation.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleCopyLink(invitation.token)}
                        className="rounded-lg p-2 hover:bg-green-100 dark:hover:bg-gray-600"
                        title="Copy invitation link"
                      >
                        {copiedToken === invitation.token ? (
                          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <Copy className="h-4 w-4 text-green-600 dark:text-green-400" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleResendInvitation(invitation.id)}
                        disabled={resendingId === invitation.id}
                        className="rounded-lg p-2 transition-opacity hover:bg-blue-100 disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-gray-600"
                        title="Resend invitation"
                      >
                        <RefreshCw
                          className={`h-4 w-4 text-blue-600 dark:text-blue-400 ${resendingId === invitation.id ? "animate-spin" : ""}`}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRevokeInvitation(invitation.id)}
                        className="rounded-lg p-2 hover:bg-red-100 dark:hover:bg-gray-600"
                        title="Revoke invitation"
                      >
                        <CircleX className="h-4 w-4 text-red-600 dark:text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
              <ShieldCheck className="h-5 w-5" />
              Account Management
            </h3>

            {!changingPassword &&
              !confirmingDeleteAccount &&
              !confirmingLeaveHousehold &&
              !confirmingDeleteHousehold && (
                <div className="flex flex-col gap-3 transition-none sm:flex-row">
                  <button
                    type="button"
                    onClick={() => setChangingPassword(true)}
                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
                  >
                    <Lock className="h-4 w-4" />
                    Change Password
                  </button>
                  {isAdmin ? (
                    <button
                      type="button"
                      onClick={() => setConfirmingDeleteHousehold(true)}
                      className="flex items-center gap-2 rounded-lg bg-yellow-600 px-4 py-2 font-medium text-white hover:bg-yellow-700"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Household
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmingLeaveHousehold(true)}
                      className="flex items-center gap-2 rounded-lg bg-yellow-600 px-4 py-2 font-medium text-white hover:bg-yellow-700"
                    >
                      <LogOut className="h-4 w-4" />
                      Leave Household
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setConfirmingDeleteAccount(true)}
                    className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700"
                  >
                    <Shredder className="h-4 w-4" />
                    Delete My Account
                  </button>
                </div>
              )}

            {confirmingDeleteHousehold && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 transition-none dark:border-yellow-700 dark:bg-yellow-900/30">
                <p className="mb-3 text-sm font-medium text-yellow-700 transition-none dark:text-yellow-400">
                  Are you sure? This will erase your household data. All members
                  will lose access. Your account will still exist and you can
                  create a new household at any time.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleDeleteHousehold}
                    disabled={deletingHousehold}
                    className="rounded-lg bg-yellow-600 px-4 py-2 text-sm font-medium text-white hover:bg-yellow-700"
                  >
                    {deletingHousehold
                      ? "Deleting..."
                      : "Yes, Delete Household"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingDeleteHousehold(false)}
                    disabled={deletingHousehold}
                    className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {confirmingLeaveHousehold && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 transition-none dark:border-yellow-700 dark:bg-yellow-900/30">
                <p className="mb-3 text-sm font-medium text-yellow-700 transition-none dark:text-yellow-400">
                  Are you sure you want to leave this household? Your account
                  will still exist, but you will lose access to all shared data.
                  You can create a new household at any time or request an
                  invite from someone else.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleLeaveHousehold}
                    disabled={leavingHousehold}
                    className="rounded-lg bg-yellow-600 px-4 py-2 text-sm font-medium text-white hover:bg-yellow-700"
                  >
                    {leavingHousehold ? "Leaving..." : "Yes, Leave Household"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingLeaveHousehold(false)}
                    disabled={leavingHousehold}
                    className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {changingPassword && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 transition-none dark:border-blue-700 dark:bg-blue-900/30">
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor="newPassword"
                      className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        autoComplete="new-password"
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 pr-10 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute top-1/2 right-2 -translate-y-1/2 rounded-sm p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        aria-label={
                          showNewPassword ? "Hide password" : "Show password"
                        }
                        tabIndex={-1}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 pr-10 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            newPassword &&
                            confirmPassword
                          ) {
                            handleUpdatePassword();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute top-1/2 right-2 -translate-y-1/2 rounded-sm p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={handleUpdatePassword}
                      disabled={
                        updatingPassword || !newPassword || !confirmPassword
                      }
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-opacity hover:bg-blue-700 disabled:pointer-events-none disabled:opacity-50"
                    >
                      {updatingPassword ? "Updating..." : "Update Password"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setChangingPassword(false);
                        setNewPassword("");
                        setConfirmPassword("");
                      }}
                      disabled={updatingPassword}
                      className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-opacity hover:bg-gray-300 disabled:pointer-events-none disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {confirmingDeleteAccount && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 transition-none dark:border-red-700 dark:bg-red-900/30">
                <p className="mb-3 text-sm font-medium text-red-700 transition-none dark:text-red-400">
                  {isAdmin
                    ? "Are you sure? This will erase your account and household data. Your members will keep their accounts and can start fresh with a new household if they choose."
                    : "Are you sure? This will erase your account and household membership."}
                </p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    disabled={deletingAccount}
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-opacity hover:bg-red-700 disabled:pointer-events-none disabled:opacity-50"
                  >
                    {deletingAccount ? "Deleting..." : "Yes, Delete My Account"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingDeleteAccount(false)}
                    disabled={deletingAccount}
                    className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-opacity hover:bg-gray-300 disabled:pointer-events-none disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
